import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
    useClientRecoveriesQuery,
    useGrantClientRecoveryMutation,
    useRevokeClientRecoveryMutation,
} from '../../services/hooks/useClientes';
import { useRole } from '../../hooks/useRole';
import type { ClientRecovery } from '../../services/api/clienteService';

interface ClientRecoveriesCardProps {
    clientId?: string;
}

type PopulatedRef = { nombre?: string } | string | null | undefined;
const refName = (ref: PopulatedRef): string =>
    (typeof ref === 'object' && ref?.nombre ? ref.nombre : '');

const daysLeft = (deadline: string): number =>
    Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

// "23 sep · 19:00" a partir de una referencia a clase, poblada o no.
const classLabel = (ev: ClientRecovery['originalEventId']): string => {
    if (ev && typeof ev === 'object' && ev.fecha) {
        return `${format(new Date(ev.fecha), "d MMM", { locale: es })}${ev.horaInicio ? ` · ${ev.horaInicio}` : ''}`;
    }
    return '';
};

const originalClassLabel = (r: ClientRecovery): string => classLabel(r.originalEventId);

// Las que el cliente se GANÓ al cancelar, frente a las que le puso el centro.
// Ya no decide quién se puede retirar —se puede cualquiera que siga
// pendiente— sino si hace falta escribir un motivo. Quien manda es
// `EARNED_RECOVERY_SOURCES` en el backend, que responde 400 igualmente.
const EARNED: ReadonlyArray<ClientRecovery['source']> = [
    'client_cancel', 'admin_cancel', 'enrollment_adjustment',
];

const SOURCE_LABELS: Partial<Record<NonNullable<ClientRecovery['source']>, string>> = {
    admin_cancel: 'cancelada por el centro',
    // P3 — valor propio y no 'admin_cancel' precisamente para poder decir el
    // motivo: la clase no se canceló por una incidencia, cayó en festivo.
    holiday: 'día festivo',
    enrollment_adjustment: 'ajuste de alta',
    manual: 'otorgada manualmente',
};

// M10 — lets the admin see at a glance whether a client has classes to recover.
// Además permite otorgar una recuperación a mano (y revocarla) — solo admin.
const ClientRecoveriesCard = ({ clientId }: ClientRecoveriesCardProps) => {
    const { data, isLoading } = useClientRecoveriesQuery(clientId);
    const { isAdmin } = useRole();
    const grant = useGrantClientRecoveryMutation(clientId);
    const revoke = useRevokeClientRecoveryMutation(clientId);

    const [showForm, setShowForm] = useState(false);
    // La recuperación que se está retirando, con su motivo a medio escribir.
    const [revoking, setRevoking] = useState<ClientRecovery | null>(null);
    const [revokeReason, setRevokeReason] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [levelId, setLevelId] = useState('');
    const [note, setNote] = useState('');

    const count = data?.count ?? 0;
    const items = data?.items ?? [];
    const levels = data?.availableLevels ?? [];
    // E4 — opcionales: un backend anterior no los manda, y entonces la tarjeta
    // se comporta exactamente como antes.
    const summary = data?.summary;
    const history = data?.history ?? [];

    const closeForm = () => {
        setShowForm(false);
        setLevelId('');
        setNote('');
    };

    const handleGrant = () => {
        if (!levelId) return;
        grant.mutate({ levelId, note: note.trim() || undefined }, { onSuccess: closeForm });
    };

    return (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
            <div className="flex items-center mb-2">
                <RefreshCw className="mr-2" size={20} />
                <h2 className="text-lg font-bold">Clases por recuperar</h2>
                {!isLoading && count > 0 && (
                    <span className="ml-3 rounded-full bg-amber-100 text-amber-800 text-sm font-bold px-2.5 py-0.5">
                        {count}
                    </span>
                )}
                {isAdmin && !showForm && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-brand-primary text-white hover:brightness-90"
                    >
                        <Plus size={14} /> Añadir
                    </button>
                )}
            </div>

            {/* Alta manual: solo niveles con plaza activa. El nivel queda como
                procedencia, no como restricción — el cliente puede canjear la
                recuperación en cualquiera de los niveles en los que esté
                inscrito (ver `rescheduleClass` en el backend). */}
            {isAdmin && showForm && (
                <div className="mt-3 mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-800">Otorgar recuperación</h3>
                        <button onClick={closeForm} className="-m-1 p-1 text-gray-400 hover:text-gray-600" aria-label="Cerrar">
                            <X size={16} />
                        </button>
                    </div>

                    {levels.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            Este cliente no tiene plazas activas, así que no podría canjear ninguna recuperación.
                        </p>
                    ) : (
                        <>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Nivel</label>
                            <select
                                value={levelId}
                                onChange={(e) => setLevelId(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded text-sm mb-3"
                            >
                                <option value="">Selecciona un nivel…</option>
                                {levels.map((nivel) => (
                                    <option key={nivel._id} value={nivel._id}>{nivel.nombre}</option>
                                ))}
                            </select>

                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Motivo <span className="text-gray-400">(opcional)</span>
                            </label>
                            <input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="p. ej. incidencia en la sala"
                                className="w-full p-2 border border-gray-300 rounded text-sm mb-3"
                            />

                            <div className="flex gap-2">
                                <button onClick={closeForm} className="flex-1 px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleGrant}
                                    disabled={!levelId || grant.isPending}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded bg-brand-primary text-white hover:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {grant.isPending && <Loader2 size={14} className="shrink-0 animate-spin" />}
                                    Otorgar
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center my-4">
                    <Loader2 className="animate-spin" />
                </div>
            ) : count === 0 ? (
                <p className="text-gray-500 text-sm flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-green-500" />
                    Este cliente no tiene clases pendientes de recuperar.
                </p>
            ) : (
                <ul className="mt-3 space-y-2">
                    {items.map((r) => {
                        const dias = daysLeft(r.rescheduleDeadline);
                        const urgente = dias <= 3;
                        const origen = originalClassLabel(r);
                        const sourceLabel = r.source ? SOURCE_LABELS[r.source] : undefined;
                        return (
                            <li
                                key={r._id}
                                className={`flex items-start justify-between gap-3 border rounded-lg p-3 ${
                                    urgente ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                                }`}
                            >
                                <div className="min-w-0">
                                    <div className="font-medium text-sm">
                                        {refName(r.levelId) || 'Clase'}
                                        {sourceLabel && (
                                            <span className="ml-2 text-xs text-gray-500">({sourceLabel})</span>
                                        )}
                                    </div>
                                    {origen && (
                                        <div className="text-xs text-gray-500">Clase original: {origen}</div>
                                    )}
                                    {r.note && (
                                        <div className="text-xs text-gray-500 italic">{r.note}</div>
                                    )}
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="text-right whitespace-nowrap">
                                        <div className={`text-xs font-semibold ${urgente ? 'text-amber-700' : 'text-gray-600'}`}>
                                            {urgente && <AlertTriangle size={12} className="inline mr-1 align-text-top" />}
                                            {dias <= 0 ? 'Caduca hoy' : `${dias} día${dias === 1 ? '' : 's'}`}
                                        </div>
                                        <div className="text-[11px] text-gray-400">
                                            hasta {format(new Date(r.rescheduleDeadline), "d MMM", { locale: es })}
                                        </div>
                                        {/* B11 — la fecha viene de la recuperación que gastó para
                                            entrar en la clase que canceló, no de ésta. Sin decirlo
                                            parece que el plazo está mal calculado. */}
                                        {r.deadlineInheritedFrom && (
                                            <div className="text-[11px] text-gray-400 italic">plazo heredado</div>
                                        )}
                                    </div>
                                    {/* Cualquiera que siga pendiente. Las que el cliente se ganó
                                        piden un motivo antes de irse; las que puso el centro, no.
                                        Siempre queda la fila en su historial. */}
                                    {isAdmin && (
                                        <button
                                            onClick={() => { setRevoking(r); setRevokeReason(''); }}
                                            disabled={revoke.isPending}
                                            title="Retirar esta recuperación"
                                            aria-label="Retirar recuperación"
                                            className="-m-1 p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* E4 — lo que ya no está pendiente. Responde de un vistazo a
                "¿cuántas ha tenido y dónde fueron a parar?", que antes exigía
                entrar en la base de datos. */}
            {!isLoading && summary && (summary.rescheduled > 0 || summary.expired > 0) && (
                <div className="mt-4 border-t border-gray-200 pt-3">
                    <button
                        onClick={() => setShowHistory((v) => !v)}
                        className="w-full flex items-center justify-between text-sm text-gray-600 hover:text-gray-900"
                    >
                        <span>
                            <span className="font-medium">{summary.rescheduled}</span> usada(s) ·{' '}
                            <span className="font-medium">{summary.expired}</span> caducada(s)
                        </span>
                        {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>

                    {showHistory && (
                        <>
                            {/* Decirlo aquí evita el "estos números no cuadran":
                                no es un saldo histórico, es lo que queda sin
                                barrer. */}
                            <p className="text-xs text-gray-400 mt-2">
                                Solo las de los últimos meses: las recuperaciones se borran pasado un tiempo desde su fecha límite.
                                {data?.historyTruncated && ' Se muestran las 50 más recientes.'}
                            </p>
                            <ul className="mt-2 space-y-2">
                                {history.map((r) => {
                                    const origen = originalClassLabel(r);
                                    const destino = classLabel(r.rescheduledEventId);
                                    const usada = r.status === 'rescheduled';
                                    const sourceLabel = r.source ? SOURCE_LABELS[r.source] : undefined;
                                    return (
                                        <li key={r._id} className="flex items-start gap-3 border border-gray-200 rounded-lg p-3">
                                            <span className={`mt-0.5 rounded-full p-1.5 ${usada ? 'text-green-600 bg-green-50' : 'text-gray-500 bg-gray-100'}`}>
                                                {usada ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                                            </span>
                                            <div className="min-w-0 text-sm">
                                                <div className="font-medium">
                                                    {refName(r.levelId) || 'Clase'}
                                                    {sourceLabel && (
                                                        <span className="ml-2 text-xs font-normal text-gray-500">({sourceLabel})</span>
                                                    )}
                                                </div>
                                                <div className="text-gray-600">
                                                    {origen ? `Ganada por cancelar ${origen}` : 'Concedida a mano'}
                                                    {usada
                                                        ? destino ? ` → usada en ${destino}` : ' → usada'
                                                        : ` → caducó el ${format(new Date(r.rescheduleDeadline), 'd MMM', { locale: es })}`}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            )}

            {/* Retirar una recuperación. La confirmación sale siempre, también
                para las que puso el centro: borrar algo de la ficha de un
                cliente no debería ser un clic suelto en una lista. El motivo
                solo se exige cuando el cliente se la ganó. */}
            {revoking && (() => {
                const earned = EARNED.includes(revoking.source);
                const missingReason = earned && !revokeReason.trim();
                return (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white p-6 rounded-lg max-w-md w-full">
                            <div className="flex items-center mb-3">
                                <AlertTriangle size={28} className="text-yellow-500 mr-3 shrink-0" />
                                <h3 className="font-bold text-lg">Retirar recuperación</h3>
                            </div>

                            <p className="text-sm text-gray-600 mb-4">
                                {refName(revoking.levelId) || 'Clase'}
                                {originalClassLabel(revoking) && <> · clase original {originalClassLabel(revoking)}</>}
                                {earned
                                    ? <span className="block mt-2 text-amber-800 bg-amber-50 rounded p-2">
                                        El cliente ganó esta recuperación al cancelar una clase. Retirarla le quita
                                        algo a lo que tenía derecho, así que queda en su historial con el motivo.
                                    </span>
                                    : <span className="block mt-2">Quedará anotado en el historial del cliente.</span>}
                            </p>

                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Motivo {earned ? <span className="text-red-600">*</span> : <span className="text-gray-400">(opcional)</span>}
                            </label>
                            <input
                                type="text"
                                value={revokeReason}
                                onChange={(e) => setRevokeReason(e.target.value)}
                                placeholder={earned ? 'p. ej. emitida por error' : 'p. ej. limpieza de festivos'}
                                className="w-full p-2 border border-gray-300 rounded text-sm mb-4"
                            />

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setRevoking(null)}
                                    disabled={revoke.isPending}
                                    className="py-2 px-4 bg-gray-300 rounded hover:bg-gray-400 disabled:opacity-50"
                                >
                                    Volver
                                </button>
                                <button
                                    onClick={() => revoke.mutate(
                                        { recoveryId: revoking._id, reason: revokeReason.trim() || undefined },
                                        { onSuccess: () => setRevoking(null) },
                                    )}
                                    disabled={revoke.isPending || missingReason}
                                    className="py-2 px-4 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center"
                                >
                                    {revoke.isPending && <Loader2 size={14} className="animate-spin mr-2" />}
                                    Retirar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default ClientRecoveriesCard;
